/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CartLineItemComponent } from '@msdyn365-commerce/components';
import { Button, getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import MsDyn365, * as Msdyn365 from '@msdyn365-commerce/core';
import { isChannelTypeB2B, TelemetryEvent } from '@msdyn365-commerce/core';
import { CartLine, DeliveryOption, ProductCatalog, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import React, { useState } from 'react';
import CheckoutCartLineDeliveryOptionsRadio from './checkout-cartLine-delivery-options-radio';

export interface ICheckoutDeliveryOptionsLineItemsProps {
    className?: string;
    moduleId: string;
    selectedItemId: string;
    context: Msdyn365.ICoreContext;
    typeName: string;
    cartLines: CartLine[];
    imageSettings: Msdyn365.IImageSettings;
    showShippingChargesForLineItems: boolean;
    resources: ICheckoutDeliveryOptionsLineItemsResources;
    deliveryOptions: DeliveryOption[];
    ariaSetSize: number;
    ariaPosInSet: number;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[]): void;
    currencyCode?: string;
    products?: SimpleProduct[];
    catalogs?: ProductCatalog[];
    gridSettings?: Msdyn365.IGridSettings;
    cartLineId?: string;
    key?: string;
}

export interface ICheckoutDeliveryOptionsLineItemsResources {
    quantityDisplayStringLabel: string;
    productDimensionTypeColor: string;
    productDimensionTypeSize: string;
    productDimensionTypeStyle: string;
    productDimensionTypeAmount: string;
    configString: string;
    discountStringText: string;
    shippingChargesText: string;
    priceFree: string;
    ariaLabel: string;
    changeButtonText?: string;
    deliveryMethodLabel?: string;
}

const _getLineItemComponent = (
    context: Msdyn365.ICoreContext,
    cartLine: CartLine,
    product: SimpleProduct,
    moduleId: string,
    typeName: string,
    showShippingChargesForLineItems: boolean,
    imageSettings: Msdyn365.IImageSettings,
    resources: ICheckoutDeliveryOptionsLineItemsResources,
    currencyCode: string,
    catalogs?: ProductCatalog[],
    gridSettings?: Msdyn365.IGridSettings,
    cartLineId?: string
): JSX.Element | undefined => {
    let productUrl = getProductUrlSync(product, context.actionContext, undefined);
    if (MsDyn365.isBrowser && isChannelTypeB2B(context.actionContext.requestContext)) {
        const fullUrl = new URL(productUrl, window.location.href);
        fullUrl.searchParams.set('catalogid', `${cartLine.CatalogId ?? 0}`);
        productUrl = fullUrl.href;
    }

    return (
        <CartLineItemComponent
            id={moduleId}
            typeName={typeName}
            context={context}
            resources={{
                sizeString: resources.productDimensionTypeSize,
                colorString: resources.productDimensionTypeColor,
                styleString: resources.productDimensionTypeStyle,
                amountString: resources.productDimensionTypeAmount,
                quantityDisplayString: resources.quantityDisplayStringLabel,
                configString: resources.configString,
                inputQuantityAriaLabel: '',
                discountStringText: resources.discountStringText,
                originalPriceText: '',
                currentPriceText: '',
                shippingChargesText: resources.shippingChargesText,
                salesAgreementPricePrompt: 'Contract price applied'
            }}
            isQuantityEditable={false}
            imageSettings={imageSettings}
            gridSettings={gridSettings as Msdyn365.IGridSettings}
            productUrl={productUrl}
            primaryImageUrl={product.PrimaryImageUrl}
            data={{
                cartLine,
                product,
                catalogs
            }}
            showShippingChargesForLineItems={showShippingChargesForLineItems}
            // telemetryContent={telemetryContent}
            priceCurrency={currencyCode}
        />
    );
};

const _getProduct = (productId: number, products: SimpleProduct[]): SimpleProduct | undefined => {
    return products.find((_product: SimpleProduct) => {
        return productId === _product.RecordId;
    });
};

export const GetLineItems: React.FC<ICheckoutDeliveryOptionsLineItemsProps> = (props: ICheckoutDeliveryOptionsLineItemsProps) => {
    const products = props.products;
    const lineClassName = props.className;
    const lineItemClassName = `${lineClassName}-item`;
    const currencyCode = props.currencyCode;
    const changeText = props.resources.changeButtonText;
    const [showDeliveryOption, setShowDeliveryOption] = useState(false);

    const handleClick = () => {
        setShowDeliveryOption(!showDeliveryOption);
    };
    if (!props.cartLines || props.cartLines.length === 0 || !products || products.length === 0 || currencyCode === undefined) {
        return null;
    }
    const deliveryCode = props.deliveryOptions.find(option => option.Code === props.selectedItemId);
    const payload = getPayloadObject(TelemetryEvent.UpdateInCart, props.telemetryContent!, changeText !== undefined ? changeText : '', '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const response = props.cartLines.map(cartLine => {
        const product = cartLine.ProductId && _getProduct(cartLine.ProductId, products);
        if (!product) {
            return null;
        }
        return (
            <div key={cartLine.LineId} className={lineItemClassName}>
                {_getLineItemComponent(
                    props.context,
                    cartLine,
                    product,
                    props.moduleId,
                    props.typeName,
                    props.showShippingChargesForLineItems,
                    props.imageSettings,
                    props.resources,
                    currencyCode,
                    props.catalogs,
                    props.gridSettings
                )}
                <div className='ms-cart-line-grp-deli-method'>{props.resources.deliveryMethodLabel}</div>
                <div className='ms-cart-line-grp-deli-change'>
                    <span>{deliveryCode?.Description}</span>
                    <Button className='ms-cart-line-grp-deli-change-btn' onClick={handleClick} title={changeText} {...attributes}>
                        {changeText}
                    </Button>
                </div>
                {showDeliveryOption ? (
                    <CheckoutCartLineDeliveryOptionsRadio
                        className='ms-cart-line-grp-deli'
                        deliveryOptions={props.deliveryOptions}
                        priceFree={props.resources.priceFree}
                        selectedItemId={props.selectedItemId}
                        typeName={props.typeName}
                        context={props.context}
                        cartLines={[cartLine]}
                        cartLineId={cartLine.LineId}
                        onChange={props.onChange}
                        ariaLabel={props.resources.ariaLabel}
                        telemetryContent={props.telemetryContent}
                        moduleId={''}
                        ariaSetSize={0}
                        ariaPosInSet={0}
                        groupName={`deliveryOptions_${cartLine.LineId}`}
                    />
                ) : null}
            </div>
        );
    });

    return <div className={lineClassName}>{response}</div>;
};
export default GetLineItems;
